import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Target, Heart, MessageSquare, Lightbulb, Globe2, Briefcase, GraduationCap, TrendingUp, Users, Settings, ArrowRight } from 'lucide-react';

const About = () => {
  const { t } = useTranslation();

  const values = [
    { icon: Target, label: t('about.company.values.quality') },
    { icon: MessageSquare, label: t('about.company.values.communication') },
    { icon: Lightbulb, label: t('about.company.values.solution') },
    { icon: Heart, label: t('about.company.values.future') }
  ];

  const competencies = [
    { icon: Ship, title: t('about.competencies.offshore') },
    { icon: Zap, title: t('about.competencies.lng') },
    { icon: Settings, title: t('about.competencies.epc') },
    { icon: Anchor, title: t('about.competencies.marine') },
    { icon: TrendingUp, title: t('about.competencies.planning') },
    { icon: Users, title: t('about.competencies.leadership') }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('about.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('about.intro')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-2 bg-amber-100 text-amber-900 font-semibold rounded-full text-sm mb-4">
                {t('about.company.established')}
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                {t('about.company.title')}
              </h2>
              <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                {t('about.company.desc').split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* Mission */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center">
                  <Target className="mr-2 text-blue-900" size={24} />
                  {t('about.company.mission.title')}
                </h3>
                <div className="space-y-3 text-gray-700">
                  {t('about.company.mission.text').split('\n\n').map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Values */}
              <div className="bg-amber-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-4">
                  {t('about.company.values.title')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {values.map((value, index) => {
                    const Icon = value.icon;
                    return (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon className="text-amber-600" size={20} />
                        <span className="font-medium text-gray-700">{value.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('about.leadership.title')}
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Executive Info Card */}
              <div className="lg:col-span-1 bg-gradient-to-br from-blue-900 to-slate-900 p-8 flex flex-col items-center justify-center text-center">
                <div className="w-32 h-32 mb-6 rounded-full bg-amber-500 flex items-center justify-center text-5xl font-bold text-white shadow-2xl">
                  MS
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Muhammet Sinan İrtemek
                </h3>
                <p className="text-amber-400 font-semibold mb-1">
                  {t('about.leadership.executive.title')}
                </p>
                <p className="text-blue-200 text-sm mb-6">
                  {t('about.leadership.executive.subtitle')}
                </p>
                <Link
                  to="/leadership"
                  className="inline-flex items-center px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all"
                  data-testid="view-executive-profile"
                >
                  View Full Profile <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>

              {/* Content */}
              <div className="lg:col-span-2 p-8">
                <div className="inline-block px-4 py-2 bg-blue-100 text-blue-900 font-semibold rounded-full text-sm mb-4">
                  {t('about.leadership.executive.specialization')}
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {t('about.leadership.executive.desc')}
                </p>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-blue-900 mb-1">15+</div>
                    <div className="text-sm text-gray-600">{t('about.leadership.executive.experience')}</div>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-amber-900 mb-1">240 MW</div>
                    <div className="text-sm text-gray-600">{t('about.leadership.executive.power')}</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-green-900 mb-1">10+</div>
                    <div className="text-sm text-gray-600">{t('about.leadership.executive.projects')}</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-purple-900 mb-1">8</div>
                    <div className="text-sm text-gray-600">{t('about.leadership.executive.countries')}</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('about.competencies.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competencies.map((comp, index) => {
              const Icon = comp.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-slate-50 p-6 rounded-lg hover:bg-blue-50 transition-colors border-2 border-transparent hover:border-blue-200"
                >
                  <Icon className="text-blue-900 mb-3" size={32} />
                  <h3 className="text-lg font-bold text-slate-900">
                    {comp.title}
                  </h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Globe2 className="mx-auto mb-6 text-amber-400" size={64} />
          <h2 className="text-3xl font-bold mb-6">
            {t('about.approach.title')}
          </h2>
          <p className="text-xl text-blue-200 leading-relaxed">
            {t('about.approach.text')}
          </p>
        </div>
      </section>
    </div>
  );
};

// Import missing icons
import { Ship, Anchor, Zap } from 'lucide-react';

export default About;
